import { memo, useMemo, useRef } from 'react';
import { Box3, Vector2, Vector2Tuple, Vector3 } from 'three';
import { Object3D } from 'three';

import {
  CommanderActionTypesEnum,
  TranslateAction,
  TranslateActionPayload,
} from '@spazious/dollhouse-commander';
import {
  DragControls as BaseDragControls,
  DragControlsProps as BaseDragControlsProps,
  useDollhouse,
  useInteractiveObjects,
  useSelection,
} from '@spazious/dollhouse-engine';
import {
  DecorationNode,
  FCompositionNode,
  FCompositionNodeImpl,
  FNode,
  FNodeTypesEnum,
  FurnitureNode,
  getNodeFromObject,
  getWorldPosition,
  isFArrangementNode,
  outlineFromNode,
} from '@spazious/dollhouse-fsetup';
import { useTranslate } from '@spazious/dollhouse-i18n';
import { isPointInNavMesh, NavMeshQuery } from '@spazious/dollhouse-navmesh';
import { join } from '@spazious/dollhouse-selector';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { emitDragPreview } from '../../core/dragPreview.js';
import { MainViewMessage } from '../../core/EventManager.js';
import { useCommander, useNodesPlacementValidator } from '../../hooks/index.js';
import { useRestrictedArea } from '../../hooks/useRestrictedArea.js';
import { moveNodesInNavMesh, NODES_PLACEMENT_ERROR } from '../../utils/index.js';
import { useEvents } from '../EventManager/index.js';

export type DraggableNodes = FurnitureNode | FCompositionNode | DecorationNode;

type DraggingNode = {
  node: DraggableNodes;
  box: Box3;
  center: Vector2;
  size: Vector2;
  theta: number;
  outline2D?: Vector2Tuple[];
};

type PreviewObject = {
  object: Object3D;
  initial: Vector3;
};

const _draggingNodes: DraggingNode[] = [];
/**
 * Scene objects moved visually while dragging. The staged translate is
 * applied once at drop, so the graph and store stay untouched during the
 * move phase.
 */
const _previewObjects: PreviewObject[] = [];
const _previewSelectors = new Set<string>();
const _size = new Vector3();
const _center = new Vector3();

const collectPreviewObjects = (object: Object3D, selectors: Set<string>) => {
  // selection wrappers (RectControls/PolygonControls meshes) carry the node in
  // their userData too, but they live in their own rotated frame and follow
  // the drag through the dragPreview channel instead
  if (object.userData?.controls) {
    return;
  }
  const node = getNodeFromObject(object);
  if (node?.selector && selectors.has(node.selector)) {
    _previewObjects.push({ object, initial: object.position.clone() });
    return;
  }
  object.children.forEach((child) => collectPreviewObjects(child, selectors));
};

const applyDragPreview = (movement: Vector3) => {
  _previewObjects.forEach(({ object, initial }) => {
    object.position.set(initial.x + movement.x, initial.y, initial.z + movement.z);
  });
};

const resetDragPreview = () => {
  _previewObjects.forEach(({ object, initial }) => {
    object.position.copy(initial);
  });
  _previewObjects.length = 0;
  _previewSelectors.clear();
  emitDragPreview(null);
};

const _samplePoint = new Vector3();

const isTranslatedPointInNavMesh = (
  point: Vector2Tuple,
  movement: Vector2Tuple,
  navMeshQuery: NavMeshQuery
) =>
  isPointInNavMesh(
    _samplePoint.set(point[0] + movement[0], 0, point[1] + movement[1]),
    navMeshQuery
  );

/**
 * Rejects a drop without the staged round-trip when the placement is provably
 * invalid — applying and rolling back a translate re-layouts and re-renders
 * the affected nodes twice, which stalls for seconds on dense arrangements.
 * Fixed-shape nodes fail exactly like the placement validator (a translated
 * outline corner outside the navmesh). Adaptive arrangements re-layout at
 * apply time and can regenerate seats that were navmesh-clipped at the old
 * position, so neither the pre-drag outline nor the current children prove
 * anything about the prospective layout; they are only rejected when the
 * translated footprint (the `size` attribute bounds every candidate seat
 * center) overlaps no navmesh polygon at all — anything closer falls
 * through to the staged validation.
 */
const isDropProvablyInvalid = (
  dragNodes: DraggingNode[],
  movement: Vector2Tuple,
  navMeshQuery: NavMeshQuery | null
): boolean => {
  if (!navMeshQuery) {
    return false;
  }
  return dragNodes.some(({ node, outline2D, center, size }) => {
    if (!outline2D?.length) {
      return false;
    }
    if (!isFArrangementNode(node)) {
      return !outline2D.every((p) => isTranslatedPointInNavMesh(p, movement, navMeshQuery));
    }
    // half the diagonal covers the oriented footprint at any rotation
    const halfDiagonal = Math.hypot(size.x, size.y) / 2;
    const result = navMeshQuery.queryPolygons(
      _samplePoint.set(center.x + movement[0], 0, center.y + movement[1]),
      { x: halfDiagonal, y: 1, z: halfDiagonal }
    );
    return result.success && result.polyCount === 0;
  });
};

export interface DragControlsProps
  extends Omit<BaseDragControlsProps, 'onDrag' | 'objects' | 'matrixWorld' | 'modifiers'> {
  /**
   * Whether to check against navmesh while dragging nodes.
   * If true, nodes cannot be moved out of navmesh at any time
   * If false, nodes can be moved out of navmesh during drag
   * But, at the end of the drag nodes should alsways be inside navesh, else the drag action is reverted
   * Default: false
   */
  liveNavMeshCheck?: boolean;
}

const draggableNodeTypes = [
  FNodeTypesEnum.Furniture,
  FNodeTypesEnum.FComposition,
  FNodeTypesEnum.Decoration,
];

const getTargetNode = (object: Object3D): DraggableNodes | null => {
  const node = getNodeFromObject(object) as DraggableNodes | null;
  if (!node) {
    return null;
  }
  return node.type === FNodeTypesEnum.Furniture && node.originator ? node.originator : node;
};

const isDraggable = (object: Object3D): boolean => !!getTargetNode(object);

const nodeReducer = (acc: DraggableNodes[], node: DraggableNodes | null): DraggableNodes[] => {
  return node && draggableNodeTypes.includes(node.type)
    ? [...acc, node.type === FNodeTypesEnum.Furniture && node.originator ? node.originator : node]
    : acc;
};

const objectNodeReducer = (acc: DraggableNodes[], object: Object3D): DraggableNodes[] => {
  return nodeReducer(acc, getTargetNode(object));
};

export const DragControls: React.FC<DragControlsProps> = memo(
  ({ enabled, liveNavMeshCheck = false, ...restProps }) => {
    const draggedRef = useRef<boolean>(false);
    const actionRef = useRef<TranslateAction | null>(null);
    const payloadRef = useRef<TranslateActionPayload | null>(null);
    const commander = useCommander();
    const events = useEvents();
    const translate = useTranslate();
    const { selected: selectedObjects } = useSelection();
    const [interactiveObjects] = useInteractiveObjects();
    const navMesh = useDollhouse(({ world }) => world?.navMesh);
    const { graph } = useGraph();
    const { isPointInRestrictedArea, confirm } = useRestrictedArea();
    const ignoreRestrictedAreas = useStore(({ settings }) => settings.ignoreRestrictedAreas);
    const matrixWorld = useDollhouse(({ scene }) => scene.matrixWorld);
    const scene = useDollhouse(({ scene }) => scene);
    const invalidate = useDollhouse(({ invalidate }) => invalidate);
    const set = useStore(({ set }) => set);
    const controls = useStore(({ controls }) => controls);

    const navMeshQuery = useMemo(() => (navMesh ? new NavMeshQuery(navMesh) : null), [navMesh]);

    const selectedNodes = useMemo(() => {
      return Array.from(
        new Set(
          [...selectedObjects].reduce<DraggableNodes[]>(objectNodeReducer, [] as DraggableNodes[])
        )
      );
    }, [selectedObjects]);

    const { validator } = useNodesPlacementValidator([]);

    const finalizeDrag = useEventCallback((action: TranslateAction) => {
      const commitDrag = () => {
        action.commit();
        _previewObjects.length = 0;
      };
      // The abort rollback can render in the same React batch as the staged
      // drop update, so from React's point of view the node props never
      // changed and the manually moved objects are not repositioned. Restore
      // them to their pre-drag positions explicitly.
      const abortDrag = () => {
        action.abort();
        resetDragPreview();
        invalidate();
      };
      const affectedNodes = action
        .affectedNodes()
        .map(({ selector }) => graph.findBySelector(selector))
        .filter((node) => node !== null) as FNode[];
      const success = validator(affectedNodes);
      if (success) {
        const movedNodes = affectedNodes.flatMap((node) => {
          const nodes = [node];
          node?.traverse((cNode) => nodes.push(cNode));
          return nodes;
        }) as FNode[];

        if (
          !ignoreRestrictedAreas &&
          movedNodes.some((node) => isPointInRestrictedArea(getWorldPosition(node)))
        ) {
          confirm((message: MainViewMessage) => {
            if (message === MainViewMessage.RestrictedAreaActionRevert) {
              abortDrag();
            } else if (message === MainViewMessage.RestrictedAreaActionIgnore) {
              commitDrag();
            }
          });
        } else {
          commitDrag();
        }
      } else {
        abortDrag();
      }
    });

    const handleDrag = useEventCallback<PickProp<BaseDragControlsProps, 'onDrag'>>(
      ({ movement, phase, object, event }) => {
        const action = actionRef.current;

        if (phase === 'start') {
          const targetNode = getTargetNode(object);
          const targetNodes = Array.from(
            new Set(
              nodeReducer(
                !(selectedObjects.includes(object) || selectedNodes.includes(targetNode!))
                  ? []
                  : selectedNodes,
                targetNode
              )
            )
          );
          _draggingNodes.length = 0;
          targetNodes.forEach((node) => {
            const dragNode: DraggingNode = {
              node: node,
              box: new Box3(),
              center: new Vector2(...node.position),
              size: new Vector2(),
              theta: node.rotation,
            };
            dragNode.outline2D = outlineFromNode(node);

            if (node.type === FNodeTypesEnum.FComposition) {
              const cNode = node as FCompositionNodeImpl;
              if (cNode.instance?.has('size')) {
                // farrangement nodes, use size attribute
                const size: Vector2Tuple = cNode.instance.getValue('size');
                dragNode.size.fromArray(size);

                dragNode.box.setFromCenterAndSize(
                  _center.set(node.position[0], 0, node.position[1]),
                  _size.set(size[0], 0, size[1])
                );
              } else {
                // fensembles, calculate bounding box
                cNode.instance.computeBoundingBox();
                dragNode.box.copy(cNode.instance.boundingBox!);
              }
            } else if (
              node.type === FNodeTypesEnum.Furniture ||
              node.type === FNodeTypesEnum.Decoration
            ) {
              // furnitures - aleady collider has the bounding box calculated
              const cNode = node as FurnitureNode;
              dragNode.box.copy(cNode.collider!.box);
            }
            dragNode.box.getSize(_size);
            dragNode.size.fromArray([_size.x, _size.z]);
            _draggingNodes.push(dragNode);
          });

          actionRef.current = commander.createAction(
            CommanderActionTypesEnum.Translate,
            join(_draggingNodes.map((node) => node.node.selector))
          );

          // if not selected, move as a group
          const asGroup = !(
            selectedObjects.includes(object) || selectedNodes.includes(targetNode!)
          );

          payloadRef.current = {
            movement: [0, 0],
            copy: !!event.altKey,
            asGroup,
          };

          // Collect the scene objects owning the affected nodes to move them
          // visually while dragging. Copy keeps the staging-driven preview
          // since it must show the staged ghost nodes.
          _previewObjects.length = 0;
          _previewSelectors.clear();
          if (!payloadRef.current.copy) {
            const shallowNodes = commander.prevState.nodes;
            _draggingNodes.forEach(({ node }) => {
              if (!node.selector) {
                return;
              }
              const shallowNode = shallowNodes[node.selector];
              if (asGroup && shallowNode && shallowNode.type !== FShallowNodeTypesEnum.FGroup) {
                // mirror TranslateAction asGroup semantics: the base node plus
                // the nodes stacked on it
                const baseSelector = shallowNode.parent ?? node.selector;
                _previewSelectors.add(baseSelector);
                Object.entries(shallowNodes).forEach(([selector, candidate]) => {
                  if (
                    candidate.type !== FShallowNodeTypesEnum.FGroup &&
                    candidate.parent === baseSelector
                  ) {
                    _previewSelectors.add(selector);
                  }
                });
              } else {
                _previewSelectors.add(node.selector);
              }
            });
            collectPreviewObjects(scene, _previewSelectors);
          }
        }

        const { success = true, selectedMovement = movement } = liveNavMeshCheck
          ? moveNodesInNavMesh(_draggingNodes, movement, navMeshQuery!)
          : {};

        if (action && success) {
          payloadRef.current!.movement = [selectedMovement.x, selectedMovement.z];
          if (payloadRef.current!.copy) {
            action.update(payloadRef.current!);
          } else if (phase === 'move') {
            applyDragPreview(selectedMovement);
            // selection wrappers (RectControls and friends) follow the same
            // movement, they render from node positions which stay untouched
            // until drop
            emitDragPreview({ selectors: _previewSelectors, movement: selectedMovement });
            invalidate();
          }
        }

        if (phase === 'move') {
          if (movement.length() > 0.5 || selectedMovement?.length() > 0.05) {
            set({ tool: ToolModeEnum.None, showNavMesh: true });
          }
          draggedRef.current = true;
        }

        if (action && phase === 'end') {
          if (
            draggedRef.current &&
            (movement.length() > 0.05 || selectedMovement?.length() > 0.05)
          ) {
            // Keep the preview objects until finalizeDrag resolves: on commit
            // the staged positions take over, on abort they must be restored
            if (payloadRef.current!.copy) {
              finalizeDrag(action);
            } else if (
              isDropProvablyInvalid(_draggingNodes, payloadRef.current!.movement, navMeshQuery)
            ) {
              events.emit('error', translate(NODES_PLACEMENT_ERROR));
              resetDragPreview();
              invalidate();
              action.abort();
            } else {
              action.update(payloadRef.current!);
              // FNode positions are applied on the next graph commit (async);
              // the validator and the stacking side effects read them, so
              // finalize after that commit
              const unsubscribe = graph.subscribe(() => {
                unsubscribe();
                // the wrappers re-render from the committed node positions now
                _previewSelectors.clear();
                emitDragPreview(null);
                finalizeDrag(action);
              });
            }
          } else {
            // Nothing staged: React won't restore the preview objects
            resetDragPreview();
            invalidate();
            action.abort();
          }
          set({ showNavMesh: false });
          actionRef.current = null;
          draggedRef.current = false;
        }
      }
    );

    return (
      <BaseDragControls
        matrixWorld={matrixWorld}
        {...restProps}
        enabled={enabled && ![ControlsModeEnum.Walk].includes(controls)}
        isDraggable={isDraggable}
        objects={interactiveObjects}
        onDrag={handleDrag}
      />
    );
  }
);
